'use client'

import Link from 'next/link'
import { useEffect, useState, type ReactNode } from 'react'

import { useMediaQuery } from '@/hooks/useIsMobile'
import type { LinkPreview } from '@/components/ui/link-preview'

interface LazyLinkPreviewProps {
  children: ReactNode
  url: string
  imageSrc: string
  width?: number
  height?: number
  className?: string
}

/**
 * A plain link that upgrades to the hover-card preview on devices that can
 * hover. The preview pulls in Radix hover-card and motion springs, which a
 * touch device can never trigger, so those only download where they're usable.
 * The server and first client render are always the plain link, which keeps the
 * anchor crawlable and the markup hydration-safe.
 */
export function LazyLinkPreview({
  children,
  url,
  className,
  ...preview
}: LazyLinkPreviewProps) {
  const canHover = useMediaQuery('(hover: hover)')
  const [Preview, setPreview] = useState<typeof LinkPreview | null>(null)

  useEffect(() => {
    if (!canHover) return
    let live = true
    void import('@/components/ui/link-preview').then((mod) => {
      if (live) setPreview(() => mod.LinkPreview)
    })
    return () => {
      live = false
    }
  }, [canHover])

  if (!Preview) {
    return (
      <Link href={url} className={className}>
        {children}
      </Link>
    )
  }
  return (
    <Preview url={url} isStatic className={className} {...preview}>
      {children}
    </Preview>
  )
}
